<template>
  <div class="data-collapse-hes">
    <v-snackbar :timeout="snackbar.timeout" :color="snackbar.color" v-model="snackbar.show">
      {{ snackbar.message }}
    </v-snackbar>

    <PageHeader
      icon="mdi-table-arrow-down"
      title="Data Collapse HES"
      subtitle="Senarai data perbelanjaan setiap isi rumah (satu baris per HID) daripada lampiran_hid_perbelanjaan"
    />

    <v-card outlined class="mb-4">
      <!-- Toolbar -->
      <v-card-title class="py-3 kad__tajuk d-flex align-center flex-wrap">
        <v-icon small class="mr-2">mdi-table</v-icon>
        <span class="subtitle-1 font-weight-medium">Senarai Data</span>
        <v-chip x-small color="grey lighten-3" class="ml-3 font-weight-medium">{{ total }} baris</v-chip>
        <v-chip x-small color="grey lighten-3" class="ml-2 font-weight-medium">{{ selectedMeasures.length }} kolum g dipilih</v-chip>
        <v-spacer></v-spacer>
        <v-btn outlined class="text-none reset-btn mr-2" @click="openColumnDialog">
          <v-icon left>mdi-view-column</v-icon>
          Pilihan Kolum
        </v-btn>
        <v-btn class="btn-teal text-none" height="36" @click="exportDialog = true">
          <v-icon left>mdi-download</v-icon>
          Muat Turun CSV
        </v-btn>
        <v-btn
          v-if="!negeriLocked"
          outlined color="teal darken-1" class="text-none ml-2" height="36"
          @click="openPreparedDialog"
        >
          <v-icon left>mdi-database-arrow-down-outline</v-icon>
          Muat Turun Sedia Ada
        </v-btn>
      </v-card-title>

      <!-- Bar penapis — susunan yang SAMA seperti Kadar Respon / Jadual Awalan:
           jalur teal, label kening di ATAS medan. Label terapung dibuang kerana
           ia beralih tempat apabila medan diisi; `persistent-placeholder`
           menggantikannya supaya medan kosong masih menunjukkan pilihannya. -->
      <v-card-text class="filter-bar py-3">
        <v-row align="end" dense>
          <v-col cols="12" sm="4" md="3">
            <div class="fld__label">Negeri</div>
            <v-autocomplete
              v-model="filters.negeri"
              :items="negeriItems"
              :placeholder="negeriLocked ? '' : 'Semua Negeri'"
              persistent-placeholder
              :disabled="negeriLocked"
              :clearable="!negeriLocked"
              :prepend-inner-icon="negeriLocked ? 'mdi-lock-outline' : undefined"
              outlined dense hide-details
              @change="onFilterChange"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <div class="fld__label">Pusingan</div>
            <v-select
              v-model="filters.pusingan"
              :items="uniquePusingan"
              outlined dense hide-details
              @change="onFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="auto" class="d-flex align-center">
            <v-btn outlined height="40" class="text-none reset-btn" @click="resetFilters">
              <v-icon small left>mdi-refresh</v-icon>
              Tetapkan Semula
            </v-btn>
          </v-col>
          <v-col v-if="negeriLocked" cols="12" sm="auto" class="d-flex align-center">
            <v-chip small label color="blue-grey lighten-5" text-color="blue-grey darken-2" class="font-weight-medium">
              <v-icon x-small left>mdi-lock-outline</v-icon>
              Negeri dikunci mengikut negeri anda
            </v-chip>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Table -->
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="total"
        :loading="loading"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        dense
        class="dch-table"
      >
        <!-- Only the measure columns currently in the horizontal viewport are
             rendered; the two spacer cells keep the scrollbar geometry correct. -->
        <template v-slot:item="{ item, index }">
          <tr>
            <td class="idx-col">{{ rowNo(index) }}</td>
            <td
              v-for="h in criteriaHeaders"
              :key="h.value"
              :class="{ 'pk-col': h.value === 'HID', 'empty-cell': isEmptyCell(item[h.value]) }"
              :title="item[h.value]"
            >
              {{ cell(item[h.value]) }}
            </td>
            <td v-if="virt.padLeft" class="spacer-col" :style="{ width: virt.padLeft + 'px', minWidth: virt.padLeft + 'px' }"></td>
            <td
              v-for="m in visibleMeasures"
              :key="m"
              class="text-end m-col"
              :class="{ 'empty-cell': isEmptyCell(item[m]) }"
            >
              {{ fmt(item[m]) }}
            </td>
            <td v-if="virt.padRight" class="spacer-col" :style="{ width: virt.padRight + 'px', minWidth: virt.padRight + 'px' }"></td>
          </tr>
        </template>
        <template v-slot:no-data>
          <div class="text-center grey--text pa-8">Tiada rekod dijumpai bagi tapisan ini</div>
        </template>
      </v-data-table>
    </v-card>

    <!-- ── Column picker dialog ─────────────────────────────────────────────── -->
    <v-dialog v-model="columnDialog" max-width="820" scrollable>
      <v-card>
        <v-card-title class="py-3 kad__tajuk">
          <v-icon small class="mr-2">mdi-view-column</v-icon>
          <span class="subtitle-1 font-weight-medium">Pilihan Kolum</span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="columnDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="py-4" style="max-height:70vh">
          <!-- Criteria columns (HID is always shown / read-only). Edits a DRAFT and
               only take effect when "Terap" is pressed. -->
          <div class="overline grey--text text--darken-1 mb-1">Kolum Kriteria</div>
          <div class="d-flex flex-wrap" style="gap:2px 18px">
            <v-checkbox
              v-for="c in allCriteria"
              :key="c.value"
              v-model="draftCriteria"
              :value="c.value"
              :label="c.value"
              :disabled="c.value === 'HID'"
              dense hide-details
              class="mt-0 pt-0"
            ></v-checkbox>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Measure (g*) columns -->
          <div class="overline grey--text text--darken-1 mb-1">Kolum Nilai (bermula dengan g)</div>
          <div class="d-flex flex-wrap align-center mb-2" style="gap:6px">
            <span class="caption grey--text mr-1">Pilih pantas:</span>
            <v-btn v-for="lv in levelButtons" :key="lv.kind"
              x-small class="text-none"
              :class="isLevelActive(lv.kind) ? 'btn-teal' : 'reset-btn'"
              :outlined="!isLevelActive(lv.kind)"
              @click="toggleLevel(lv.kind)"
            >
              <v-icon v-if="isLevelActive(lv.kind)" x-small left>mdi-check</v-icon>
              {{ lv.label }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn x-small text color="grey darken-1" class="text-none" @click="draftMeasures = []">Kosongkan</v-btn>
          </div>
          <v-autocomplete
            v-model="draftMeasures"
            :items="filteredMeasureItems"
            :search-input.sync="measureSearch"
            item-text="text"
            item-value="value"
            label="Cari & pilih kolum (kod atau keterangan)"
            placeholder="Taip cth: g01, makanan, 011111…"
            multiple
            outlined dense hide-details clearable
            no-filter
            :menu-props="{ maxHeight: 340 }"
            no-data-text="Taip untuk mencari kolum…"
          >
            <!-- Cap rendered chips so selecting a whole level (e.g. 2472 items) stays snappy -->
            <template v-slot:selection="{ item, index }">
              <v-chip
                v-if="index < 15"
                :key="chipLabel(item)"
                small close
                @click:close="removeMeasure(chipLabel(item))"
              >{{ chipLabel(item) }}</v-chip>
              <span v-else-if="index === 15" class="caption grey--text mx-1">
                +{{ draftMeasures.length - 15 }} lagi…
              </span>
            </template>
            <template v-slot:item="{ item, attrs, on }">
              <v-list-item v-on="on" v-bind="attrs" dense>
                <v-list-item-content>
                  <v-list-item-title class="body-2">{{ item.value }}</v-list-item-title>
                  <v-list-item-subtitle v-if="item.keterangan" class="caption">{{ item.keterangan }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip x-small label outlined>{{ item.level }}</v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-autocomplete>
          <div class="caption grey--text mt-2">
            {{ draftMeasures.length }} kolum g dipilih · {{ allMeasures.length }} tersedia.
            Perubahan hanya digunakan bila anda tekan <b>Terap</b>.
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="py-3">
          <v-spacer></v-spacer>
          <v-btn text class="text-none" @click="columnDialog = false">Tutup</v-btn>
          <v-btn class="btn-teal text-none" @click="applyColumns">Terap</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Export dialog ────────────────────────────────────────────────────── -->
    <v-dialog v-model="exportDialog" max-width="520">
      <v-card>
        <v-card-title class="py-3 kad__tajuk">
          <v-icon small class="mr-2">mdi-file-delimited-outline</v-icon>
          <span class="subtitle-1 font-weight-medium">Muat Turun CSV</span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="exportDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          <div class="body-2 mb-3">Kolum manakah untuk dieksport? (tapisan Negeri &amp; Pusingan semasa dikekalkan)</div>
          <v-radio-group v-model="exportMode" class="mt-0" hide-details>
            <v-radio value="selected" class="mb-2">
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">Kolum dipilih sahaja</div>
                  <div class="caption grey--text">Kriteria + {{ selectedMeasures.length }} kolum g yang dipilih dalam jadual</div>
                </div>
              </template>
            </v-radio>
            <v-radio value="all">
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">Semua kolum</div>
                  <div class="caption grey--text">Kriteria + semua {{ allMeasures.length }} kolum g (fail besar)</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
          <v-alert v-if="exportMode === 'all' && !filters.negeri" dense text type="warning" class="caption mt-3 mb-0">
            Tiada tapisan Negeri — eksport semua kolum untuk semua negeri boleh menghasilkan fail yang sangat besar.
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="py-3">
          <v-spacer></v-spacer>
          <v-btn text class="text-none" @click="exportDialog = false">Batal</v-btn>
          <v-btn class="btn-teal text-none" :loading="exporting" @click="doExport">
            <v-icon small left>mdi-download</v-icon>
            Muat Turun
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Prepared "everything" CSV dialog ─────────────────────────────────── -->
    <v-dialog v-model="preparedDialog" max-width="720" @input="v => { if (!v) stopPreparedPoll() }">
      <v-card>
        <v-card-title class="py-3 kad__tajuk">
          <v-icon small class="mr-2">mdi-database-arrow-down-outline</v-icon>
          <span class="subtitle-1 font-weight-medium">Muat Turun Sedia Ada CSV</span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="closePreparedDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-3">
          <div v-if="!preparedDatasets.length" class="caption grey--text py-3 text-center">
            Tiada set data disediakan.
          </div>

          <!-- One block per prepared dataset; new criteria added on the server
               simply appear here as another row. -->
          <div
            v-for="(ds, i) in preparedDatasets"
            :key="ds.key"
            class="prepared-row py-3"
            :class="{ 'border-top': i > 0 }"
          >
            <div class="d-flex align-start">
              <div style="min-width:0" class="flex-grow-1">
                <div class="body-2 font-weight-medium blue-grey--text text--darken-3">
                  Kriteria: {{ ds.criteriaText }}
                </div>

                <!-- generating -->
                <div v-if="ds.state && ds.state.preparing" class="d-flex align-center mt-1">
                  <v-progress-circular indeterminate size="14" width="2" color="green darken-2" class="mr-2"></v-progress-circular>
                  <span class="caption grey--text">
                    Sedang menjana… {{ (ds.state.rows || 0).toLocaleString('en-MY') }} baris ditulis
                  </span>
                </div>

                <!-- ready -->
                <div v-else-if="ds.exists && ds.meta" class="caption grey--text mt-1">
                  Dijana: {{ fmtDate(ds.meta.generatedAt) }} ·
                  {{ (ds.meta.rows || 0).toLocaleString('en-MY') }} baris ·
                  {{ (ds.meta.columns || 0).toLocaleString('en-MY') }} kolum ·
                  {{ fmtBytes(ds.meta.bytes) }}
                </div>

                <!-- not generated -->
                <div v-else class="caption grey--text mt-1">
                  Belum dijana — tekan “Jana” untuk menyediakannya.
                </div>

                <div v-if="ds.state && ds.state.error" class="caption red--text mt-1">
                  Ralat penjanaan: {{ ds.state.error }}
                </div>
              </div>

              <div class="d-flex align-center ml-3" style="gap:4px">
                <v-btn
                  x-small text color="grey darken-1" class="text-none"
                  :disabled="ds.state && ds.state.preparing"
                  @click="regeneratePrepared(ds.key)"
                >
                  <v-icon x-small left>mdi-refresh</v-icon>
                  {{ ds.exists ? 'Jana Semula' : 'Jana' }}
                </v-btn>
                <v-btn
                  small class="btn-teal text-none"
                  :disabled="!ds.exists || (ds.state && ds.state.preparing)"
                  :loading="preparedDownloading === ds.key"
                  @click="downloadPreparedNow(ds.key)"
                >
                  <v-icon small left>mdi-download</v-icon>
                  Muat Turun
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="py-3">
          <v-spacer></v-spacer>
          <v-btn text class="text-none" @click="closePreparedDialog">Tutup</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  getColumns, fetchTable, downloadCsv,
  preparedCsvStatus, prepareCsv, csvTicket, preparedDownloadUrl,
} from "./dataCollapseHesData";
import getPusingan from "@/services/pusingan";
import lookupField from "@/services/lookupField";
import { negeriLock } from "@/services/negeriScope";

const pusinganSvc = new getPusingan();
const lookupSvc = new lookupField();

export default {
  name: "DataCollapseHes",
  data: () => ({
    snackbar: { show: false, message: "", color: "success", timeout: 3000 },

    // filters (Wajaran-style values)
    filters: { negeri: null, pusingan: null },
    listNegeri: [],
    dataTapisPusingan: [],
    // PP/PO are pinned to their own negeri (hiesuser.state): filter disabled, only
    // that negeri shown. Enforced again server-side by modulePolicy('dataCollapse').
    negeriLocked: false,
    lockKodneg: null,

    // table
    ready: false,
    loading: false,
    items: [],
    total: 0,
    options: { page: 1, itemsPerPage: 25, sortBy: [], sortDesc: [] },
    optionsTimer: null,
    reqSeq: 0,

    // horizontal virtualization of the measure columns
    MEASURE_W: 120,   // fixed px width per measure column (must match .m-col CSS)
    VIRT_MIN: 60,     // below this many measures, render them all
    VIRT_BUFFER: 6,   // extra columns rendered off-screen each side
    scrollLeft: 0,
    wrapperW: 1200,
    leadW: 0,         // px taken by the "#" + criteria columns
    scrollTimer: null,
    scrollEl: null,

    // columns — `selected*` are APPLIED (drive the table); `draft*` are what the
    // Pilihan Kolum dialog edits and only become applied on "Terap".
    allCriteria: [],
    allMeasures: [],
    selectedCriteria: [],
    selectedMeasures: [],
    draftCriteria: [],
    draftMeasures: [],
    defaults: { criteria: [], measures: [] },

    // Quick-pick level buttons (toggle whole levels in the draft).
    levelButtons: [
      { kind: "k2", label: "2-digit" },
      { kind: "k2w", label: "2-digit (wajaran)" },
      { kind: "k4", label: "4-digit" },
      { kind: "k6", label: "6-digit" },
      { kind: "k8", label: "8-digit" },
      { kind: "all", label: "Semua" },
    ],

    // dialogs
    columnDialog: false,
    measureSearch: "",
    exportDialog: false,
    exportMode: "selected",
    exporting: false,

    // prepared CSV files — a LIST of datasets, each with its own criteria, so new
    // criteria added server-side show up here with no frontend change.
    preparedDialog: false,
    preparedDatasets: [],
    preparedDownloading: null, // key currently downloading
    preparedPollTimer: null,
  }),

  computed: {
    // Negeri dropdown — 01..16 only (same rule as the Wajaran page). When the role
    // is negeri-locked (PP/PO), only their own negeri is offered.
    negeriItems() {
      const all = (this.listNegeri || []).map(n => ({
        text: `${n.negeriID} - ${n.negeri}`,
        value: n.negeriID,
      }));
      if (this.negeriLocked && this.lockKodneg) {
        const pad = s => (String(s).length === 1 ? "0" + s : String(s));
        const k = pad(this.lockKodneg);
        const only = all.filter(o => pad(o.value) === k);
        return only.length ? only : all.filter(o => o.value === this.lockKodneg);
      }
      return all;
    },

    // Pusingan values — distinct ACTIVE codes + a "{min}-{max} (Semua)" entry,
    // identical logic to the Wajaran design.
    uniquePusingan() {
      const rows = this.dataTapisPusingan || [];
      const active = rows.filter(i => i.status == "1" || i.status == 1);
      let pus = [...new Set(active.map(i => i.pusingan))].filter(Boolean).sort();
      if (pus.length > 1) {
        const min = parseInt(pus[0]);
        const max = parseInt(pus[pus.length - 1]);
        pus.push(`${min}-${max} (Semua)`);
      }
      return pus;
    },

    // Chosen criteria columns (always rendered — there are only a handful).
    criteriaHeaders() {
      return this.allCriteria
        .filter(c => this.selectedCriteria.includes(c.value))
        .map(c => ({
          text: c.value,
          value: c.value,
          measure: false,
          class: c.value === "HID" ? "pk-col" : undefined,
        }));
    },
    /**
     * Horizontal virtualization window for the measure columns.
     * Rendering 3,157 columns × 25 rows is ~79k cells and locks the browser, so we
     * render only the slice inside the horizontal viewport (+ buffer) and replace the
     * rest with two spacer cells of the exact skipped width, which keeps the scrollbar
     * and every column position identical to a fully-rendered table.
     */
    virt() {
      const total = this.selectedMeasures.length;
      const W = this.MEASURE_W;
      if (total <= this.VIRT_MIN) {
        return { active: false, start: 0, end: total, padLeft: 0, padRight: 0 };
      }
      const first = Math.floor(Math.max(0, this.scrollLeft - this.leadW) / W);
      const span = Math.ceil(this.wrapperW / W);
      const start = Math.max(0, first - this.VIRT_BUFFER);
      const end = Math.min(total, first + span + this.VIRT_BUFFER);
      return { active: true, start, end, padLeft: start * W, padRight: (total - end) * W };
    },
    visibleMeasures() {
      return this.selectedMeasures.slice(this.virt.start, this.virt.end);
    },
    // What v-data-table renders in <thead> — mirrors the row template exactly.
    headers() {
      const out = [
        { text: "#", value: "__idx", sortable: false, class: "idx-col", width: 56 },
        ...this.criteriaHeaders,
      ];
      if (this.virt.padLeft) {
        out.push({ text: "", value: "__padL", sortable: false, class: "spacer-col", width: this.virt.padLeft });
      }
      this.visibleMeasures.forEach(m => {
        out.push({ text: m, value: m, measure: true, align: "end", class: "m-col", width: this.MEASURE_W });
      });
      if (this.virt.padRight) {
        out.push({ text: "", value: "__padR", sortable: false, class: "spacer-col", width: this.virt.padRight });
      }
      return out;
    },

    // Options for the measure autocomplete: show the (draft) selected ones when idle,
    // and search matches (capped) while typing — keeps the menu fast with ~3157 cols.
    filteredMeasureItems() {
      const sel = this.draftMeasures;
      const q = (this.measureSearch || "").toLowerCase().trim();
      if (!q) {
        // Idle → show the selected columns so their chips keep their labels.
        return this.allMeasures.filter(m => sel.includes(m.value)).slice(0, 300);
      }
      // Searching → matches FIRST (what the user is after), then any selected
      // column not in the matches (kept so its chip still shows a label).
      const matches = this.allMeasures.filter(
        m => m.value.toLowerCase().includes(q) ||
          (m.keterangan && m.keterangan.toLowerCase().includes(q))
      ).slice(0, 400);
      const shown = new Set(matches.map(m => m.value));
      const selExtra = this.allMeasures.filter(m => sel.includes(m.value) && !shown.has(m.value));
      return [...matches, ...selExtra];
    },

    // True while ANY prepared dataset is being (re)generated on the server.
    preparedBusy() {
      return this.preparedDatasets.some(d => d.state && d.state.preparing);
    },
  },

  watch: {
    // Coalesce the two-tick sortBy/sortDesc bursts from v-data-table header clicks.
    // Column changes do NOT auto-refetch — they apply only on "Terap" (applyColumns).
    options: {
      handler() {
        clearTimeout(this.optionsTimer);
        this.optionsTimer = setTimeout(() => this.fetchData(), 0);
      },
      deep: true,
    },
  },

  async mounted() {
    try {
      // Filter lookups (same services as the Wajaran page).
      const negeriRes = await lookupSvc.getNegeri();
      this.listNegeri = ((negeriRes && negeriRes.data && negeriRes.data.data) || [])
        .filter(n => parseInt(n.negeriID) <= 16);

      // Negeri-locked roles (PP/PO): pin to their own negeri + disable the filter.
      const lock = negeriLock(this.$store, "dataCollapse");
      if (lock.locked) {
        this.negeriLocked = true;
        this.lockKodneg = lock.kodneg;
        const pad = s => (String(s).length === 1 ? "0" + s : String(s));
        const match = this.listNegeri.find(n => pad(n.negeriID) === pad(lock.kodneg));
        this.filters.negeri = match ? match.negeriID : lock.kodneg;
      }

      const pusRes = await pusinganSvc.getAllPusingan();
      this.dataTapisPusingan = (pusRes && pusRes.data && pusRes.data.data) || [];
      const semua = this.uniquePusingan.find(p => String(p).includes("(Semua)"));
      this.filters.pusingan = semua || this.uniquePusingan[0] || null;

      // Column catalogue + defaults.
      const cols = await getColumns();
      this.allCriteria = cols.criteria || [];
      this.allMeasures = cols.measures || [];
      this.defaults = cols.defaults || { criteria: [], measures: [] };
      this.selectedCriteria = [...(this.defaults.criteria || [])];
      this.selectedMeasures = [...(this.defaults.measures || [])];
    } catch (e) {
      console.error("DataCollapseHes init failed:", e);
      this.snackbar = { show: true, message: "Gagal memuatkan tetapan modul", color: "error", timeout: 4000 };
    }

    this.ready = true;
    this.fetchData();
    this.$nextTick(this.attachScroll);
    window.addEventListener("resize", this.onTableScroll, { passive: true });
  },

  updated() {
    // no-op when the wrapper element hasn't changed
    this.attachScroll();
  },

  methods: {
    pusinganParam() {
      const p = this.filters.pusingan;
      if (!p || String(p).includes("(Semua)")) return "";
      return p;
    },

    async fetchData() {
      if (!this.ready) return;
      const seq = ++this.reqSeq;
      this.loading = true;
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options;
        const res = await fetchTable({
          page, itemsPerPage, sortBy, sortDesc,
          negeri: this.filters.negeri,
          pusingan: this.pusinganParam(),
          columns: this.selectedMeasures,
        });
        if (seq !== this.reqSeq) return; // discard stale response
        this.items = res.items || [];
        this.total = res.total || 0;
        // Column widths are only final once rows are painted — re-measure so the
        // frozen-HID offset and the virtualization window use real widths.
        this.$nextTick(this.measureLead);
      } catch (e) {
        if (seq !== this.reqSeq) return;
        console.error("fetchData failed:", e);
        this.snackbar = { show: true, message: "Gagal memuatkan data", color: "error", timeout: 3000 };
      } finally {
        if (seq === this.reqSeq) this.loading = false;
      }
    },

    onFilterChange() {
      this.options.page = 1;
      this.fetchData();
    },

    resetFilters() {
      // Locked roles stay pinned to their own negeri.
      if (!this.negeriLocked) this.filters.negeri = null;
      const semua = this.uniquePusingan.find(p => String(p).includes("(Semua)"));
      this.filters.pusingan = semua || this.uniquePusingan[0] || null;
      this.onFilterChange();
    },

    // ── Column dialog (edits DRAFT; applied on Terap) ─────────────────────────
    openColumnDialog() {
      this.measureSearch = "";
      // Snapshot the applied selection into the draft the dialog edits.
      this.draftCriteria = [...this.selectedCriteria];
      this.draftMeasures = [...this.selectedMeasures];
      this.columnDialog = true;
    },
    // The g-column names belonging to a quick-pick level.
    //   k2 = 2-digit groups (g01..g13,g90)  k2w = their wajaran variants
    //   k4/k6 = 4-/6-digit groups           k8 = 8-digit items (~2472)  all = every measure
    // The five level pools PARTITION every measure column (16+16+150+503+2472 =
    // 3157), so turning them all on lights up "Semua". The jum_* grand totals ride
    // with the 2-digit groups they roll up — otherwise they'd belong to no level
    // and "Semua" could never become active.
    levelPool(kind) {
      if (kind === "all") return this.allMeasures.map(m => m.value);
      const test = {
        k2: v => /^g\d{2}$/.test(v) || /^jum_g\d+g\d+$/.test(v),
        k2w: v => /^g\d{2}_wajaran$/.test(v) || /^jum_g\d+g\d+_wajaran$/.test(v),
        k4: v => /^g\d{4}$/.test(v),
        k6: v => /^g\d{6}$/.test(v),
        k8: v => /^g\d{8}$/.test(v),
      }[kind];
      if (!test) return [];
      return this.allMeasures.filter(m => test(m.value)).map(m => m.value);
    },
    // A level is "active" when every column in its pool is in the draft.
    isLevelActive(kind) {
      const pool = this.levelPool(kind);
      if (!pool.length) return false;
      const set = new Set(this.draftMeasures);
      return pool.every(v => set.has(v));
    },
    // Toggle a whole level in the draft: remove all if fully present, else add all.
    toggleLevel(kind) {
      const pool = this.levelPool(kind);
      if (!pool.length) return;
      const set = new Set(this.draftMeasures);
      if (pool.every(v => set.has(v))) pool.forEach(v => set.delete(v));
      else pool.forEach(v => set.add(v));
      this.draftMeasures = [...set];
    },
    // Selection-slot helpers (item may be the object or, for values outside the
    // capped items list, the raw value string).
    chipLabel(item) {
      return item && typeof item === "object" ? item.value : item;
    },
    removeMeasure(value) {
      this.draftMeasures = this.draftMeasures.filter(v => v !== value);
    },
    applyColumns() {
      // Commit the draft → applied, then refetch once with the new column set.
      this.selectedCriteria = [...this.draftCriteria];
      this.selectedMeasures = [...this.draftMeasures];
      this.columnDialog = false;
      // Column set changed → reset the horizontal window and re-measure the lead.
      if (this.scrollEl) this.scrollEl.scrollLeft = 0;
      this.scrollLeft = 0;
      this.$nextTick(this.measureLead);
      this.fetchData();
    },

    // ── Horizontal virtualization ─────────────────────────────────────────────
    attachScroll() {
      const el = this.$el && this.$el.querySelector(".dch-table .v-data-table__wrapper");
      if (!el || el === this.scrollEl) return;
      if (this.scrollEl) this.scrollEl.removeEventListener("scroll", this.onTableScroll);
      this.scrollEl = el;
      el.addEventListener("scroll", this.onTableScroll, { passive: true });
      this.measureLead();
    },
    // Throttled to ~60fps with setTimeout rather than requestAnimationFrame: rAF is
    // paused in background/headless tabs, which would leave the "already scheduled"
    // guard stuck forever and freeze the window at its last position.
    onTableScroll() {
      if (this.scrollTimer) return;
      this.scrollTimer = setTimeout(() => {
        this.scrollTimer = null;
        if (!this.scrollEl) return;
        this.scrollLeft = this.scrollEl.scrollLeft;
        this.wrapperW = this.scrollEl.clientWidth || 1200;
      }, 16);
    },
    // Width of the leading (#, criteria) cells, so the window maps to the right slice.
    // Also publishes the "#" column width as --idx-w, which positions the frozen HID.
    measureLead() {
      if (!this.scrollEl) return;
      const ths = this.scrollEl.querySelectorAll("thead th");
      const lead = 1 + this.criteriaHeaders.length;
      let w = 0;
      for (let i = 0; i < lead && i < ths.length; i++) w += ths[i].offsetWidth;
      this.leadW = w;
      this.wrapperW = this.scrollEl.clientWidth || 1200;
      if (ths.length) this.scrollEl.style.setProperty("--idx-w", ths[0].offsetWidth + "px");
    },

    // ── Formatting ─────────────────────────────────────────────────────────────
    // Running row number across pages (matches the "#" column).
    rowNo(index) {
      const { page, itemsPerPage } = this.options;
      return (page - 1) * (itemsPerPage > 0 ? itemsPerPage : 0) + index + 1;
    },
    isEmptyCell(v) {
      return v === null || v === undefined || v === "";
    },
    cell(v) {
      return v === null || v === undefined || v === "" ? "—" : v;
    },
    fmt(v) {
      if (v === null || v === undefined || v === "") return "—";
      const n = Number(v);
      if (isNaN(n)) return v;
      return n.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // ── Export ───────────────────────────────────────────────────────────────
    exportFilename() {
      const ng = this.filters.negeri || "semua";
      const p = this.pusinganParam() || "semua";
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "").replace(/(\d{8})(\d{4})/, "$1-$2");
      return `data-collapse-hes_negeri-${ng}_p-${p}_${this.exportMode}_${stamp}.csv`;
    },
    async doExport() {
      this.exporting = true;
      try {
        const blob = await downloadCsv({
          negeri: this.filters.negeri,
          pusingan: this.pusinganParam(),
          columns: this.selectedMeasures,
          mode: this.exportMode,
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = this.exportFilename();
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        this.exportDialog = false;
      } catch (e) {
        console.error("export failed:", e);
        this.snackbar = { show: true, message: "Gagal menjana CSV", color: "error", timeout: 3500 };
      } finally {
        this.exporting = false;
      }
    },

    // ── Prepared "everything" CSV ─────────────────────────────────────────────
    openPreparedDialog() {
      this.preparedDialog = true;
      this.loadPreparedStatus();
    },
    closePreparedDialog() {
      this.preparedDialog = false;
      this.stopPreparedPoll();
    },
    async loadPreparedStatus() {
      try {
        const res = await preparedCsvStatus();
        this.preparedDatasets = (res && res.datasets) || [];
        if (this.preparedBusy) this.startPreparedPoll();
        else this.stopPreparedPoll();
      } catch (e) {
        console.error("preparedCsvStatus failed:", e);
      }
    },
    startPreparedPoll() {
      if (this.preparedPollTimer) return;
      this.preparedPollTimer = setInterval(() => this.loadPreparedStatus(), 3000);
    },
    stopPreparedPoll() {
      if (this.preparedPollTimer) {
        clearInterval(this.preparedPollTimer);
        this.preparedPollTimer = null;
      }
    },
    async regeneratePrepared(key) {
      try {
        await prepareCsv(key);
        await this.loadPreparedStatus(); // reflects preparing → starts polling
        this.startPreparedPoll();
      } catch (e) {
        console.error("prepareCsv failed:", e);
        this.snackbar = { show: true, message: "Gagal memulakan penjanaan CSV", color: "error", timeout: 3500 };
      }
    },
    async downloadPreparedNow(key) {
      this.preparedDownloading = key;
      try {
        const { ticket } = await csvTicket(key);
        // Direct browser navigation → streams to disk (no in-memory blob for a big file).
        const a = document.createElement("a");
        a.href = preparedDownloadUrl(ticket);
        a.download = "";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e) {
        console.error("download prepared failed:", e);
        this.snackbar = { show: true, message: "Gagal memuat turun CSV sedia ada", color: "error", timeout: 3500 };
      } finally {
        this.preparedDownloading = null;
      }
    },
    fmtDate(iso) {
      if (!iso) return "-";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleString("en-MY", { dateStyle: "medium", timeStyle: "short" });
    },
    fmtBytes(b) {
      b = Number(b) || 0;
      if (b < 1024) return b + " B";
      const u = ["KB", "MB", "GB", "TB"];
      let i = -1;
      do { b /= 1024; i++; } while (b >= 1024 && i < u.length - 1);
      return b.toFixed(1) + " " + u[i];
    },
  },

  beforeDestroy() {
    this.stopPreparedPoll();
    window.removeEventListener("resize", this.onTableScroll);
    if (this.scrollEl) this.scrollEl.removeEventListener("scroll", this.onTableScroll);
    if (this.scrollTimer) clearTimeout(this.scrollTimer);
  },
};
</script>

<style scoped>
/* Kepala kad & dialog — jalur putih dengan ikon teal, corak yang sama seperti
   halaman lain. */
.kad__tajuk {
  border-bottom: 1px solid #dbeeeb;
}
.kad__tajuk .v-icon {
  color: #037769;
}

/* ---------- Bar penapis ---------- */
.filter-bar {
  background: #f7fcfb;
  border-top: 1px solid #dbeeeb;
  border-bottom: 1px solid #dbeeeb;
}
.fld__label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}
.filter-bar >>> .v-input fieldset { border-color: #cfeae7; }
.filter-bar >>> .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.filter-bar >>> .v-select__selection--comma { color: #21403c; }
.filter-bar >>> .v-input__prepend-inner .v-icon,
.filter-bar >>> .v-input__append-inner .v-icon { color: #7aa8a2; }

/* Butang teal — menggantikan `color="primary"` (biru Vuetify). */
.btn-teal.v-btn {
  background: #037769 !important;
  color: #ffffff !important;
  letter-spacing: normal;
}
.btn-teal.v-btn:hover { background: #026254 !important; }
/* Vuetify memberi `.v-icon` warnanya sendiri yang tidak mewarisi warna butang —
   pada latar teal ia hampir tidak kelihatan. */
.btn-teal.v-btn .v-icon { color: #ffffff !important; }
.btn-teal.v-btn--disabled {
  background: #cfd8dc !important;
  color: rgba(0, 0, 0, .38) !important;
}
.btn-teal.v-btn--disabled .v-icon { color: rgba(0, 0, 0, .38) !important; }
.reset-btn.v-btn--outlined {
  border-color: #cfeae7;
  color: #037769;
  letter-spacing: normal;
}
.reset-btn.v-btn--outlined:hover {
  background: #eef8f6;
  border-color: #037769;
}
.reset-btn.v-btn--outlined .v-icon { color: #037769; }

/* separator between prepared-dataset rows */
.prepared-row.border-top {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
/* ---- Listing table: same look as Pamer Data HIES "Eksport CSV" preview ----
   (many columns → horizontal scroll, sticky header + sticky row-number column) */
.dch-table >>> .v-data-table__wrapper {
  overflow: auto;
  max-height: 440px;
  border: 1px solid #dbeeeb;
  border-radius: 6px;
  background: #fff;
}
.dch-table >>> .v-data-table__wrapper > table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: var(--fs-sm);
  white-space: nowrap;
  min-width: 100%;
}
.dch-table >>> .v-data-table__wrapper > table th,
.dch-table >>> .v-data-table__wrapper > table td {
  padding: 5px 10px !important;
  height: auto !important;
  font-size: var(--fs-sm) !important;
  border-bottom: 1px solid #eee;
  border-right: 1px solid #f2f2f2;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dch-table >>> .v-data-table__wrapper > table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #e6f6f5;
  font-weight: 700;
  color: #01463e !important;
  text-align: left;
  border-bottom: 2px solid #cfeae7 !important;
}
/* rows need an OPAQUE background: the frozen cells inherit it, otherwise the
   scrolling columns would show through them */
.dch-table >>> .v-data-table__wrapper > table tbody tr { background: #fff; }
.dch-table >>> .v-data-table__wrapper > table tbody tr:nth-child(even) { background: #fafafa; }
.dch-table >>> .v-data-table__wrapper > table tbody tr:hover { background: #eef8f6 !important; }
/* ---- Frozen leading columns: "#" and HID stay put while scrolling right ----
   --idx-w is written from JS (measureLead) with the "#" column's real width, so
   HID docks exactly against it whatever the row numbers grow to. */
.dch-table >>> .idx-col {
  position: sticky;
  left: 0;
  z-index: 1;
  background: inherit;
  color: #90a4ae;
  text-align: right;
  /* MUST be a fixed width: with table-layout:auto + min-width:100% this column
     otherwise absorbs all the slack (measured 1181px once), and HID — which docks
     at var(--idx-w) — gets pushed off to the right. */
  width: 56px !important;
  min-width: 56px !important;
  max-width: 56px !important;
  box-sizing: border-box;
}
.dch-table >>> .pk-col {
  position: sticky;
  left: var(--idx-w, 56px);
  z-index: 1;
  background: #fff8e1;
  font-weight: 600;
  /* a right edge so the frozen block reads as one unit against scrolling data */
  box-shadow: 2px 0 3px -1px rgba(0, 0, 0, 0.12);
}
/* header corners must sit above BOTH the sticky header row and the frozen cells */
.dch-table >>> .v-data-table__wrapper > table thead th.idx-col { z-index: 4; background: #e6f6f5; }
.dch-table >>> .v-data-table__wrapper > table thead th.pk-col { z-index: 4; background: #ffecb3; }
/* fixed-width measure columns — the virtualization maths depends on MEASURE_W */
.dch-table >>> .m-col {
  width: 120px !important;
  min-width: 120px !important;
  max-width: 120px !important;
}
/* spacer cells stand in for the off-screen columns (keeps scroll width correct) */
.dch-table >>> .spacer-col {
  padding: 0 !important;
  border-right: none !important;
  max-width: none !important;
}
.dch-table >>> .empty-cell { color: #bbb; }
</style>
